/**
 * Sentry telemetry service.
 *
 * Initialises the Sentry React SDK when a DSN is provided via
 * VITE_SENTRY_DSN. In dev (or when the env var is absent) the
 * module exports safe no-ops so consuming code never has to
 * null-check.
 */

import * as Sentry from '@sentry/react';

export function initTelemetry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info('[Telemetry] VITE_SENTRY_DSN not set — telemetry disabled');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_NODE_ENV || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: import.meta.env.VITE_NODE_ENV === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

export function trackEvent(
  name: string,
  properties?: Record<string, string>,
): void {
  Sentry.addBreadcrumb({
    category: 'app.event',
    message: name,
    data: properties,
    level: 'info',
  });
}

export function trackException(error: Error, properties?: Record<string, string>): void {
  Sentry.captureException(error, {
    extra: properties,
  });
}

export function trackMetric(
  name: string,
  average: number,
  properties?: Record<string, string>,
): void {
  Sentry.addBreadcrumb({
    category: 'app.metric',
    message: `${name}: ${average}`,
    data: { ...properties, value: String(average) },
    level: 'info',
  });
}
